import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/Button";
import { InputSwitch } from "primereact/inputswitch";
import PaymentSummary from "../../components/Invoice/PaymentSummary";
import PaymentCalendar from "../../components/Invoice/PaymentCalendar";
import PaymentStatus from "../../components/Invoice/PaymentStatus";
import UpcomingPayments from "../../components/Invoice/UpcomingPayments";
import PaymentTimeline from "../../components/Invoice/PaymentTimeline";
import FinancialProjections from "../../components/Invoice/FinancialProjections";
import DateFilter from "../../components/Invoice/DateFilter";
import AgendaView from "../../components/Invoice/AgendaView";
import { useDispatch } from "react-redux";
import { Card } from "primereact/card";

import {
  getPaymentScheduleData,
  getPaymentSummary,
  getCalendarData,
  getAgendaViewData,
  getUpcomingPayments,
  getTimelineData,
  getFinancialProjections,
} from "../../service/paymentSchedule/PaymentScheduleService";
import moment from "moment";

const InvoicePaymentSchedulePage: React.FC = () => {
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);

  // View mode state
  const [isAgendaView, setIsAgendaView] = useState<boolean>(false);

  // State for each data section
  const [summaryData, setSummaryData] = useState<any>(null);
  const [calendarData, setCalendarData] = useState<any>([]);
  const [agendaData, setAgendaData] = useState<any>(null);
  const [upcomingPayments, setUpcomingPayments] = useState<any[]>([]);
  const [timelineData, setTimelineData] = useState<any[]>([]);
  const [financialProjections, setFinancialProjections] = useState<any>(null);
  const [currentMonth, setCurrentMonth] = useState<string>("");
  const [currentYear, setCurrentYear] = useState<number>(2025);
  const [today, setToday] = useState<number>(new Date().getDate());
  const [agendaPagination, setAgendaPagination] = useState({
    first: 0,
    rows: 5,
    page: 1,
    sortBy:0,
    sortProperty: undefined,
  });

  // Date range for API filtering
  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: "",
    endDate: "",
  });

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Handle view mode toggle
  const handleViewToggle = (checked: boolean) => {
    setIsAgendaView(checked);
    if (checked && dateRange.startDate && dateRange.endDate) {
      fetchAgendaData();
    }
  };

  // Handle date range changes from the DateFilter component OR AgendaView navigation
  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setDateRange({ startDate, endDate });

    // Extract month and year from startDate for calendar sync
    const [day, month, year] = startDate.split("/").map(Number);
    setCurrentMonth(monthNames[month - 1]);
    setCurrentYear(year);
  };

  // Handle month change from calendar component
  const handleMonthChange = (monthNumber: number, year: number) => {
    setCurrentMonth(monthNames[monthNumber - 1]);
    setCurrentYear(year);
  };

  function getMonthBoundsWithMoment(monthName: string, year = moment().year()) {
    const start = moment()
      .year(year)
      .month(monthName)
      .startOf("month")
      .format("DD/MM/YYYY");

    const end = moment()
      .year(year)
      .month(monthName)
      .endOf("month")
      .format("DD/MM/YYYY");

    return { start, end };
  }

  const fetchCalendarData = async () => {
    const { start, end } = getMonthBoundsWithMoment(currentMonth,currentYear);
    const calendarRes = await getCalendarData(dispatch, {
      startDate: start,
      endDate: end,
    });
    setCalendarData(calendarRes?.calendarData || []);
  };

  const fetchAgendaData = async () => {
    if (!dateRange.startDate || !dateRange.endDate) return;

    const agendaRes = await getAgendaViewData(dispatch, {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      page: agendaPagination.page,
      pageSize: agendaPagination.rows,
      sortBy: agendaPagination.sortBy == 1 ? "ASC" : "DESC",
      sortProperty: agendaPagination.sortProperty,
    });

    setAgendaData(agendaRes);
  };

  const handleAgendaPageChange = (e: any) => {
    setAgendaPagination({
      ...e,
      first: e.first + 1,
      rows: e.rows,
      page: e.page + 1,
    });
  };

  const onSort = (e: any) => {
    setAgendaPagination({
      ...e,
      sortBy: e.sortOrder,
      sortProperty: e.sortField,
    });
  };

  // Fetch data based on selected date range
  const fetchDataForDateRange = async () => {
    setError(null);

    if (!dateRange.startDate || !dateRange.endDate) {
      return;
    }

    // Call the API with the specified date range
    const response = await getPaymentScheduleData(dispatch, {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });

    if (response) {
      const data = response;
      setSummaryData(data.summary);
      setUpcomingPayments(data.upcomingPayments || []);
      setTimelineData(data.timelineData || []);
      setFinancialProjections(data.financialProjections);
      setCurrentMonth(data.currentMonth);
      setCurrentYear(data.currentYear);
      setToday(data.today);
    } else {
      await fetchDataSeparately();
    }

    // Fetch agenda data if agenda view is active
    if (isAgendaView) {
      await fetchAgendaData();
    }
  };

  // Updated separate data fetching function
  const fetchDataSeparately = async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const startDate = formatDate(firstDay);
      const endDate = formatDate(lastDay);

      setDateRange({ startDate, endDate });
      setCurrentMonth(monthNames[now.getMonth()]);
      setCurrentYear(now.getFullYear());
    }

    setToday(new Date().getDate());

    // Fetch payment summary
    const summaryResponse = await getPaymentSummary(dispatch);
    if (summaryResponse) {
      setSummaryData(summaryResponse.result);
    }

    // Fetch calendar data with specified date range
    const calendarResponse = await getCalendarData(dispatch, {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });

    if (calendarResponse) {
      setCalendarData(calendarResponse.result.calendarData || []);
    }

    // Fetch upcoming payments
    const upcomingResponse = await getUpcomingPayments(dispatch, 3);
    if (upcomingResponse) {
      setUpcomingPayments(upcomingResponse || []);
    }

    // Fetch timeline data
    const timelineResponse = await getTimelineData(dispatch);
    if (timelineResponse) {
      setTimelineData(timelineResponse || []);
    }

    // Fetch financial projections
    const projectionsResponse = await getFinancialProjections(dispatch);
    if (projectionsResponse) {
      setFinancialProjections(projectionsResponse);
    }
  };

  // Format date helper
  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    fetchCalendarData();
  }, [currentMonth,currentYear]);

  // Effect to fetch data when date range changes
  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      fetchDataForDateRange();
    }
  }, [dateRange, isAgendaView]);

  // Fetch agenda data when switching to agenda view
  useEffect(() => {
    if (isAgendaView && dateRange.startDate && dateRange.endDate) {
      fetchAgendaData();
    }
  }, [isAgendaView, dateRange, currentMonth, agendaPagination]);

  // Initial data fetch
  useEffect(() => {
    const initializeData = async () => {
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const startDate = formatDate(firstDay);
      const endDate = formatDate(lastDay);

      setDateRange({ startDate, endDate });
      setCurrentMonth(monthNames[now.getMonth()]);
      setCurrentYear(now.getFullYear());
    };

    initializeData();
  }, []);

  if (error && !summaryData) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-6">
          {error || "Failed to load payment data. Please try again later."}
        </div>
        <Button
          variant="primary"
          className="bg-gold text-white hover:bg-gold"
          onClick={fetchDataForDateRange}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="p-0 min-h-screen">
      <h1 className="text-xl font-bold text-gray-800 mb-4">
        Invoice Payment Schedule
      </h1>

      <div className="flex w-full items-center justify-between mb-4">
        {/* Date Filter Component */}
        <DateFilter onDateRangeChange={handleDateRangeChange} />

        {/* Export Button */}
        {/* <Button
          variant="primary"
          className="flex items-center bg-gold text-white hover:bg-gold"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mr-1"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          <span>Export Data</span>
        </Button> */}
      </div>

      {summaryData && (
        <>
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <PaymentSummary
              totalInvoiced={summaryData.totalInvoiced}
              invoiceCount={summaryData.invoiceCount}
              totalPaid={summaryData.totalPaid}
              paidInvoiceCount={summaryData.paidInvoiceCount}
              upcomingPayments={summaryData.upcomingPayments}
              nextPaymentDays={summaryData.nextPaymentDays}
            />
          </div>

          {/* View Toggle Switch - positioned above main content */}
          <div className="mb-4">
            <div className="flex items-center space-x-3">
              <span
                className={`text-sm font-medium ${
                  !isAgendaView ? "text-gold" : "text-gray-500"
                }`}
              >
                Calendar View
              </span>
              <InputSwitch
                checked={isAgendaView}
                onChange={(e) => handleViewToggle(e.value)}
                className="scale-90"
              />
              <span
                className={`text-sm font-medium ${
                  isAgendaView ? "text-gold" : "text-gray-500"
                }`}
              >
                Agenda View
              </span>
            </div>
          </div>

          {/* Calendar and side panel - always visible */}
          {!isAgendaView && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
              <div className="lg:col-span-3 bg-white rounded-md shadow border border-gray-200">
                <PaymentCalendar
                  month={currentMonth}
                  year={currentYear}
                  calendarData={calendarData}
                  today={today}
                  onMonthChange={handleMonthChange}
                  currentMonth={currentMonth}
                  currentYear={currentYear}
                  setCurrentMonth={(monthNum: number) => {
                    setCurrentMonth(monthNames[monthNum - 1]);
                  }}
                />
              </div>
              <div>
                <Card className="border h-full card_content_padding">
                  <PaymentStatus />
                  <UpcomingPayments payments={upcomingPayments} />
                </Card>
              </div>
            </div>
          )}

          {/* Agenda Table - conditionally visible */}
          {isAgendaView && (
            <div className="mb-6">
              <div className="bg-white rounded-md shadow border border-gray-200 p-4">
                <AgendaView
                  items={agendaData?.items || []}
                  startDate={agendaData?.startDate || dateRange.startDate}
                  endDate={agendaData?.endDate || dateRange.endDate}
                  currentMonth={currentMonth}
                  currentYear={currentYear}
                  onMonthChange={handleMonthChange}
                  onDateRangeChange={handleDateRangeChange}
                  pagination={
                    agendaData?.pagination || {
                      totalRecords: 0,
                      totalPages: 0,
                      currentPage: agendaPagination.first,
                      pageSize: agendaPagination.rows,
                      hasNext: false,
                      hasPrevious: false,
                    }
                  }
                  onPageChange={handleAgendaPageChange}
                  onSort={onSort}
                  sortField={agendaPagination.sortProperty}
                  sortBy={agendaPagination.sortBy}
                />
              </div>
            </div>
          )}

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Payment Timeline & Forecast
            </h2>
            <div className="bg-white rounded-md shadow border border-gray-200 p-4">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1">
                  <PaymentTimeline chartData={timelineData} today={today} />
                </div>
                <div className="md:w-1/3 border-t md:border-t-0 md:border-l border-gray-200 pt-6 md:pt-0 md:pl-6">
                  {financialProjections && (
                    <FinancialProjections
                      monthly={financialProjections?.monthly}
                      quarterly={financialProjections?.quarterly}
                      annual={financialProjections?.annual}
                      reliability={financialProjections?.reliability}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default InvoicePaymentSchedulePage;
